import { helpActions } from "./actions";

const text = (value) => typeof value === "string" && value.trim().length > 0;
const identifier = (value) =>
  typeof value === "string" && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);

export function isPublicAsset(value) {
  if (!text(value) || /[\\\s]/.test(value)) return false;
  try {
    const url = new URL(value, "https://help.invalid");
    const local = value.startsWith("/") && !value.startsWith("//");
    return (
      (local || value.startsWith("https://")) &&
      url.protocol === "https:" &&
      !url.username &&
      !url.password &&
      !url.search &&
      !url.hash
    );
  } catch {
    return false;
  }
}

export function validateManifest(entries) {
  if (!Array.isArray(entries)) return ["manifest: expected an array"];
  const errors = [];
  const ids = new Set(entries.map((entry) => entry?.id).filter(text));
  entries.forEach((entry, index) => {
    const prefix = `${index}:${entry?.id || "unknown"}`;
    const fail = (field) => errors.push(`${prefix}: invalid ${field}`);
    if (!entry || typeof entry !== "object") {
      fail("entry");
      return;
    }
    ["id", "slug"].forEach((field) => {
      if (!identifier(entry[field])) fail(field);
    });
    ["title", "summary", "outcome", "version", "reviewedRelease"].forEach(
      (field) => {
        if (!text(entry[field])) fail(field);
      },
    );
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(entry.reviewedAt || "") ||
      !Number.isFinite(Date.parse(entry.reviewedAt)) ||
      new Date(entry.reviewedAt).toISOString().slice(0, 10) !== entry.reviewedAt
    )
      fail("reviewedAt");
    if (!["tutorial", "example"].includes(entry.kind)) fail("kind");
    if (
      entry.interactiveExample !== undefined &&
      (entry.kind !== "example" || entry.interactiveExample !== "marked-answer")
    )
      fail("interactiveExample");
    if (!["draft", "published"].includes(entry.status)) fail("status");
    if (!["general", "teacher", "approved-school"].includes(entry.availability))
      fail("availability");
    ["audiences", "keywords", "related"].forEach((field) => {
      if (!Array.isArray(entry[field]) || !entry[field].every(text))
        fail(field);
    });
    if (!entry.audiences?.length) fail("audiences");
    if (
      entry.durationMinutes !== undefined &&
      (!Number.isFinite(entry.durationMinutes) || entry.durationMinutes <= 0)
    )
      fail("durationMinutes");
    if (entries.filter((other) => other?.id === entry.id).length > 1)
      fail("duplicate id");
    if (
      entries.filter(
        (other) => other?.slug === entry.slug && other?.kind === entry.kind,
      ).length > 1
    )
      fail("duplicate slug");
    if (entry.action !== undefined && !Object.hasOwn(helpActions, entry.action))
      fail("action");
    if (
      Array.isArray(entry.related) &&
      entry.related.some((id) => !ids.has(id) || id === entry.id)
    )
      fail("related reference");
    if (!Array.isArray(entry.steps) || !entry.steps.length) fail("steps");
    else
      entry.steps.forEach((step, stepIndex) => {
        if (
          !step ||
          !identifier(step.id) ||
          !text(step.title) ||
          !text(step.body)
        )
          fail(`steps[${stepIndex}]`);
        if (entry.steps.filter((other) => other?.id === step?.id).length > 1)
          fail(`steps[${stepIndex}].duplicate id`);
        if (
          step?.screenshot?.caption !== undefined &&
          !text(step.screenshot.caption)
        )
          fail(`steps[${stepIndex}].screenshot.caption`);
        if (
          step?.screenshot &&
          (!isPublicAsset(step.screenshot.src) || !text(step.screenshot.alt))
        )
          fail(`steps[${stepIndex}].screenshot`);
      });
    if (
      entry.leadScreenshot &&
      (!isPublicAsset(entry.leadScreenshot.src) ||
        !text(entry.leadScreenshot.alt) ||
        (entry.leadScreenshot.caption !== undefined &&
          !text(entry.leadScreenshot.caption)))
    )
      fail("leadScreenshot");
    if (
      entry.downloads !== undefined &&
      (!Array.isArray(entry.downloads) ||
        entry.downloads.some(
          (download) =>
            !download ||
            !isPublicAsset(download.src) ||
            !text(download.label) ||
            !text(download.description),
        ))
    )
      fail("downloads");
    if (entry.video) {
      if (!isPublicAsset(entry.video.src)) fail("video.src");
      if (
        entry.video.poster !== undefined &&
        !isPublicAsset(entry.video.poster)
      )
        fail("video.poster");
      if (!text(entry.video.transcript)) fail("video.transcript");
      if (
        !isPublicAsset(entry.video.captions?.src) ||
        !text(entry.video.captions?.label) ||
        entry.video.captions?.language !== "en"
      )
        fail("video.captions");
    }
  });
  return errors;
}

export function publishedContent(entries, report = console.error) {
  const errors = validateManifest(entries);
  errors.forEach((error) => report(`Help content: ${error}`));
  if (!Array.isArray(entries)) return [];
  const invalid = new Set(errors.map((error) => Number(error.split(":")[0])));
  return entries.filter(
    (entry, index) => !invalid.has(index) && entry.status === "published",
  );
}
